import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useStripe, CardElement, useElements } from '@stripe/react-stripe-js';
import { Form } from 'components/base';
import Card from 'components/base/card';
import Typography from 'components/base/typography';
import classNames from 'classnames';
import SETTINGS from 'data/settings';
import sleep from 'utils/sleep';
import stripeApi from '../api/stripe';
import CheckoutAffirmMessage from './checkoutAffirmMessage';

export const PAYMENT_TYPE = {
  CARD: 'card',
  AFFIRM: 'affirm',
};

const PaymentTypeTab = ({ type, active, onClick }) => {
  const { icon, label } = useMemo(() => {
    switch (type) {
      case PAYMENT_TYPE.CARD:
        return {
          icon: (
            <svg
              width="21"
              height="16"
              role="presentation"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 16 16"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M0 4a2 2 0 012-2h12a2 2 0 012 2H0zm0 2v6a2 2 0 002 2h12a2 2 0 002-2V6H0zm3 5a1 1 0 011-1h1a1 1 0 110 2H4a1 1 0 01-1-1z"
              />
            </svg>
          ),
          label: 'Credit Card',
        };
      case PAYMENT_TYPE.AFFIRM:
        return {
          icon: (
            <svg
              width="21"
              height="16"
              viewBox="0 0 21 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              focusable="false"
              className="p-Logo p-Logo--md p-TabIcon TabIcon"
            >
              <path
                d="M16.8928 16H1.96714C1.58723 16 1.24042 15.7795 1.07315 15.4316C0.287891 13.7983 -0.0767903 11.9879 0.0134624 10.171C0.103715 8.35413 0.645932 6.59061 1.58903 5.04668C2.53214 3.50275 3.84506 2.22926 5.40409 1.3463C6.96312 0.463334 8.71686 0 10.5 0C12.2831 0 14.0369 0.463334 15.5959 1.3463C17.1549 2.22926 18.4679 3.50275 19.411 5.04668C20.3541 6.59061 20.8963 8.35413 20.9865 10.171C21.0768 11.9879 20.7121 13.7983 19.9268 15.4316C19.7596 15.7795 19.4128 16 19.0329 16H16.8928Z"
                fill="white"
              />
              <path
                d="M4.12211 14.75C3.36355 13.5916 2.93219 12.2495 2.87372 10.8658C2.81524 9.4822 3.13181 8.10848 3.78992 6.89011C4.44803 5.67174 5.42319 4.65405 6.61216 3.94479C7.80114 3.23552 9.15969 2.86107 10.544 2.86107C11.9283 2.86107 13.2869 3.23552 14.4758 3.94479C15.6648 4.65405 16.64 5.67174 17.2981 6.89011C17.9562 8.10848 18.2728 9.4822 18.2143 10.8658C18.1558 12.2495 17.7245 13.5916 16.9659 14.75H19.1152C19.8292 13.2941 20.1607 11.6804 20.0787 10.0608C19.9966 8.44124 19.5037 6.86926 18.6462 5.49305C17.7888 4.11683 16.5952 2.98173 15.178 2.19473C13.7607 1.40773 12.1664 0.994766 10.5455 0.994766C8.92449 0.994766 7.33023 1.40773 5.91295 2.19473C4.49567 2.98173 3.30207 4.11683 2.44466 5.49305C1.58726 6.86926 1.09429 8.44124 1.01224 10.0608C0.930185 11.6804 1.26174 13.2941 1.97566 14.75H4.12211Z"
                fill="#4A4AF4"
              />
              <path
                d="M10.6736 6.01144C9.27565 6.01144 7.66654 6.67138 6.79281 7.36774L7.58936 9.04745C8.2898 8.40645 9.42273 7.85869 10.4435 7.85869C11.4148 7.85869 11.9493 8.1821 11.9493 8.83767C11.9493 9.27471 11.5939 9.52529 10.9226 9.58647C8.40484 9.81811 6.44769 10.6062 6.44769 12.5394C6.44769 14.0735 7.55295 15 9.37176 15C9.91852 15.0007 10.4556 14.8558 10.9278 14.58C11.4 14.3043 11.7903 13.9077 12.0585 13.431V14.7494H14.3272V9.22227C14.3272 6.93944 12.74 6.01727 10.6736 6.01727V6.01144ZM10.011 13.2271C9.28293 13.2271 8.87374 12.9124 8.87374 12.3967C8.87374 11.3186 10.1843 11.0593 11.9027 11.0593C11.9027 12.1869 11.1483 13.2271 10.0096 13.2271H10.011Z"
                fill="#060809"
              />
            </svg>
          ),
          label: 'Monthly Payments',
        };
      default:
        return {};
    }
  }, [type]);

  return (
    <Card asLink className={classNames({ 'active shadow-sm': active })} onClick={onClick}>
      <div className="card-inline h-100 align-items-center" style={{ margin: 8 }}>
        <div style={{ width: 28, marginTop: -2 }}>{icon}</div>
        <Typography.H6>{label}</Typography.H6>
      </div>
    </Card>
  );
};

const CheckoutCreditCard = props => {
  const stripe = useStripe();
  const elements = useElements();

  const [isComplete, setIsComplete] = useState(false);
  const [selectedPaymentType, setSelectedPaymentType] = useState(props.paymentTypes[0]);

  /**
   *  -1 - loading state
   *   0 - new card
   *  id - credit card id
   */
  const [selectedSavedCardId, setSelectedSavedCardId] = useState(-1);
  const [saveCard, setSaveCard] = useState(true);

  useEffect(() => {
    if (props.cards.length > 0) {
      setSelectedSavedCardId(value => {
        // when you entering new card, or still selected card is in the list - use this
        if (value === 0 || props.cards.find(card => card.id === value)) {
          return value;
        }
        // otherwise use first available card
        return props.cards[0].id;
      });
    } else {
      setSelectedSavedCardId(value => {
        // when cards are changed and there is no more previously selected card, use new card
        if (value >= 0) {
          return 0;
        }
        // keep loading if loading
        return value;
      });
    }
  }, [props.cards]);

  useEffect(() => {
    if (props.amountInCents === 0) {
      props.innerRef.current = {
        isValid: true,
        createPaymentData: async () => {
          return {
            paymentData: {},
            paymentType: '---',
          };
        },
      };
    } else if (selectedPaymentType === PAYMENT_TYPE.AFFIRM) {
      props.innerRef.current = {
        isValid: true,
        createPaymentData: async ({ accessToken, shippingAddress }) => {
          const { data, error } = await stripeApi(accessToken).createPaymentIntent(
            'affirm',
            props.amountInCents,
            props.currency,
          );

          if (error) {
            return {
              paymentError: error,
            };
          }

          const result = await stripe.confirmAffirmPayment(data.client_secret, {
            payment_method: {
              // Billing information is optional but recommended to pass in.
              billing_details: {
                email: shippingAddress.email,
                name: shippingAddress.name,
                address: {
                  line1: shippingAddress.street,
                  line2: shippingAddress.street2,
                  city: shippingAddress.city,
                  state: shippingAddress.state,
                  country: shippingAddress.country_code,
                  postal_code: shippingAddress.zip,
                },
              },
            },
            // Shipping information is optional but recommended to pass in.
            shipping: {
              name: shippingAddress.name,
              address: {
                line1: shippingAddress.street,
                line2: shippingAddress.street2,
                city: shippingAddress.city,
                state: shippingAddress.state,
                country: shippingAddress.country_code,
                postal_code: shippingAddress.zip,
              },
            },
            // Return URL where the customer should be redirected after the authorization.
            return_url: `${SETTINGS.HOST}/cart/checkout?state=success`,
          });

          console.log(result);

          if (result?.error) {
            return {
              paymentError: result.error.message,
            };
          }

          sleep();

          return {};
        },
      };
    } else if (selectedSavedCardId > 0) {
      props.innerRef.current = {
        isValid: true,
        createPaymentData: async () => {
          return {
            paymentData: {
              saved_card: {
                id: selectedSavedCardId,
              },
            },
            paymentType: 'Saved Credit Card',
          };
        },
      };
    } else if (isComplete) {
      props.innerRef.current = {
        isValid: true,
        createPaymentData: async ({ shippingAddress }) => {
          const tokenParams = { name: shippingAddress.name };

          const card = elements.getElement(CardElement);

          const payload = await stripe.createToken(card, tokenParams);

          if (payload.token) {
            return {
              paymentData: {
                stripe: {
                  source: payload.token.id,
                  save_card: saveCard,
                },
              },
              paymentType: 'Credit Card',
            };
          }

          console.error(payload.error);

          return {
            paymentError:
              payload.error?.message ??
              'Oops, unknown error when trying to authorize a credit card payment.',
          };
        },
      };
    } else {
      props.innerRef.current = {
        isValid: false,
      };
    }
  }, [
    props.amountInCents,
    props.currency,
    selectedPaymentType,
    selectedSavedCardId,
    isComplete,
    saveCard,
  ]);

  const handleOnChangeCard = e => {
    if (e.complete) {
      setIsComplete(true);
    } else {
      setIsComplete(false);
    }
  };

  const handleOnChangeSavedCard = e => {
    setSelectedSavedCardId(parseInt(e.target.value, 10));
  };

  const handleOnSaveCardChange = () => {
    setSaveCard(val => !val);
  };

  return (
    <>
      {props.paymentTypes.length > 1 && (
        <div className="card-list">
          {props.paymentTypes.map(paymentType => (
            <PaymentTypeTab
              key={paymentType}
              type={paymentType}
              active={selectedPaymentType === paymentType}
              onClick={e => {
                e.preventDefault();
                setSelectedPaymentType(paymentType);
              }}
            />
          ))}
        </div>
      )}

      <div className={classNames({ 'd-none': selectedPaymentType !== PAYMENT_TYPE.CARD })}>
        {props.cards.length > 0 && (
          <>
            <Form.Group>
              <Form.Label>Your saved cards</Form.Label>
              <Form.Select value={selectedSavedCardId} onChange={handleOnChangeSavedCard}>
                {props.cards.map(card => (
                  <Form.SelectOption key={card.id} value={card.id}>
                    {`${card.brand} ${card.number} (${card.name} ${card.expiration})`}
                  </Form.SelectOption>
                ))}
                <Form.SelectOption value={0}>ENTER A NEW CARD</Form.SelectOption>
              </Form.Select>
            </Form.Group>
          </>
        )}
        <div className={selectedSavedCardId > 0 ? 'd-none' : 'd-block mt-2'}>
          <CardElement
            options={{
              hidePostalCode: true,
              style: { base: { lineHeight: 1.5 } },
            }}
            onChange={handleOnChangeCard}
            className="stripe-element-credit-card"
          />
          <Form.Row className="mt-2">
            <Form.Col xs={8}>
              <Form.Group>
                <Form.Input
                  type="checkbox"
                  name="creditcard"
                  placeholder="Save my info for next time"
                  checked={saveCard}
                  onChange={handleOnSaveCardChange}
                />
              </Form.Group>
            </Form.Col>
            <Form.Col xs={4} className="text-right">
              <img
                src="/static/img/powered_by_stripe.svg"
                alt="Powered by Stripe"
                className="mw-100"
              />
            </Form.Col>
          </Form.Row>
        </div>
      </div>

      {props.paymentTypes.indexOf(PAYMENT_TYPE.AFFIRM) >= 0 && (
        <div className={classNames({ 'd-none': selectedPaymentType !== PAYMENT_TYPE.AFFIRM })}>
          <CheckoutAffirmMessage
            amount={props.amount}
            amountInCents={props.amountInCents}
            currency={props.currency}
            withDescription
          />
        </div>
      )}
    </>
  );
};

CheckoutCreditCard.propTypes = {
  innerRef: PropTypes.object,
  amount: PropTypes.number,
  amountInCents: PropTypes.number,
  currency: PropTypes.string,
  paymentTypes: PropTypes.array,
  cards: PropTypes.array,
};

CheckoutCreditCard.defaultProps = {
  cards: [],
  paymentTypes: [PAYMENT_TYPE.CARD],
};

export default CheckoutCreditCard;
